import {Button, InputAdornment, makeStyles, TextField}                                                                                                   from '@material-ui/core';
import Collapse                                                                                                                                          from '@material-ui/core/Collapse';
import Paper                                                                                                                                             from "@material-ui/core/Paper";
import Tab                                                                                                                                               from '@material-ui/core/Tab';
import Tabs                                                                                                                                              from '@material-ui/core/Tabs';
import MenuIcon                                                                                                                                          from '@material-ui/icons/Menu';
import ToggleButton                                                                                                                                      from '@material-ui/lab/ToggleButton';
import Decimal                                                                                                                                           from 'decimal.js';
import React, {ChangeEvent, Dispatch, MutableRefObject, SetStateAction, useEffect, useRef, useState, MouseEvent}                                         from 'react';
import styled                                                                                                                                            from "styled-components";
import {v4 as uuidv4}                                                                                                                                    from 'uuid';
import {Client}                                                                                                                                          from '../../../../api/api';
import {theme}                                                                                                                                           from "../../../../bootstrap/Theme";
import {useBoolean}                                                                                                                                      from '../../../../hooks/utils/useBoolean';
import {MarketPrices, NewOrderRequest, Order, OrderBookEntry, OrderBookRequest, OrderBookSnapshotResponse, OrderRequestParameters, OrderSide, OrderType} from '../../../../trade/types';

export const MarketWidgetElement      = styled(Paper)`
  width           : 21.8rem;
  justify-content : center;
  min-height      : 280px;
  margin          : ${(p) => theme(p).spacing(1, 1, 1)};
  padding         : ${(p) => theme(p).spacing(1, 2)};
`;

export const MarketWidgetElementRow   = styled.div`
  width           : 100%;
  display         : flex;
  justify-content : center;
  position        : relative;
  flex-wrap       : wrap;
`;

const MarketWidgetElementRowSeparated = styled.div`
  width           : 100%;
  display         : flex;
  justify-content : space-between;
  position        : relative;
  flex-wrap       : wrap;
  margin          : ${(p) => theme(p).spacing(1.25, 0)};
`;

const MarketWidgetElementInstrument   = styled.p`
  font-size  : large;
  margin     : 0.5rem;
  text-align : center;
`;

const MarketWidgetElementCloseButton  = styled.button`
  border     : none;
  background : none;
  margin     : 0.5rem;
  position   : absolute;
  right      : 0;
  cursor     : pointer;
`;

const MarketWidgetSpread              = styled.div`
  width        : 4rem;
  height       : 1.5rem;
  border-style : solid;
  border-width : thin;
  position     : absolute;
  z-index      : 1;
  background   : white;
`;

const MarketWidgetElementLastUpdated  = styled.p`
  font-size     : x-small;
  text-align    : right;
  margin-top    : 0;
  margin-bottom : 0;
`;

const MarketWidgetOrderQuantityExtSellPrice = styled.div`
  margin-top    : auto;
  margin-bottom : auto;
  width         : 20%;
  font-size     : small;
  text-align    : center;
  color         : red;
  cursor        : pointer;
`;

const MarketWidgetOrderQuantityExtBuyPrice  = styled.div`
  margin-top    : auto;
  margin-bottom : auto;
  width         : 20%;
  font-size     : small;
  text-align    : center;
  color         : green;
  cursor        : pointer;
`;

const MarketWidgetOrderQuantityExtSpread    = styled.div`
  margin-top    : auto;
  margin-bottom : auto;
  width         : 20%;
  font-size     : small;
  text-align    : center;
  color         : blue;
`;

interface MarketWidgetOrderQuantityProps {
  instrumentTicker  : string
  quantityTicker    : string
  onChangeHandler   : (e: ChangeEvent<HTMLInputElement>) => void
}

const OrderQuantityStyles       = makeStyles({
  root: {
    width: '100%',
    backgroundColor: 'white',
    marginTop: '1rem'
  }
})

const MarketWidgetOrderQuantity = (p: MarketWidgetOrderQuantityProps) => {
  const classes = useRef(OrderQuantityStyles());

  return (
    <>
      <TextField
        id={"MarketWidgetOrderQuantity_" + p.instrumentTicker}
        label="Quantity"
        className={classes.current.root}
        defaultValue={1}
        size="small"
        variant="outlined"
        onChange={p.onChangeHandler}
        InputProps={{
          endAdornment: <InputAdornment position="end">{p.quantityTicker}</InputAdornment>,
          inputProps: { min: 0, pattern: "^[0-9]*.?[0-9]*$" }
        }}
      />
    </>
  )
}

interface MarketWidgetLimitPriceProps {
  baseAssetTicker     : string
  value               : string
  instrumentTicker    : string
  quoteCurrencyTicker : string
  onChangeHandler     : (e: ChangeEvent<HTMLInputElement>) => void
}

const LimitPriceClasses         = makeStyles({
  root  : {
    width           : '100%',
    backgroundColor : 'white',
    marginTop       : '1rem'
  }
});

const MarketWidgetLimitPrice    = (p: MarketWidgetLimitPriceProps) => {
  const [ controlledValue, setControlledValue ] = useState<string>(""),
        numericRegex: RegExp                    = /^[0-9]*\.?[0-9]*$/,
        classes                                 = useRef(LimitPriceClasses());

  useEffect(()=>{
    if (p.value === "nan") {
      setControlledValue("");
    }
    try {
      const candidateValue = new Decimal(p.value);
      if (!candidateValue.isNaN()) {
        setControlledValue(p.value)
      }
    } catch (err) {}
  }, [p.value])

  return (
    <>
      <TextField
        id={"MarketWidgetLimitPrice_" + p.instrumentTicker}
        label="Limit Price"
        error={!(p.value.match(numericRegex)) ? true : false}
        value={controlledValue}
        className={classes.current.root}
        size="small"
        variant="outlined"
        onChange={p.onChangeHandler}
        InputProps={{
          endAdornment: <InputAdornment position="end">{p.quoteCurrencyTicker}</InputAdornment>,
          inputProps: { min: 0, pattern: "^[0-9]*.?[0-9]*$" }
        }}
      />
    </>
  )
}

interface MarketWidgetElementPriceProps {
  client?               : Client
  instrumentID          : number
  marketPrices          : MarketPrices
  marketPriceWebSocket  : MutableRefObject<WebSocket | null>
  orderSide             : OrderSide
  orderType             : OrderType
  quantity              : number
  setOrders?            : Dispatch<SetStateAction<{ [orderID: string]: Order }>>
}

const ElementPriceClasses       = makeStyles({
  root      : {
    width           : '50%',
    height          : '6rem',
    display         : 'flex',
    justifyContent  : 'center',
    borderStyle     : 'solid',
    borderWidth     : 'thin',
    cursor          : 'pointer',
    backgroundColor : 'white'
  },
  buyOrder  : {
    position      : 'absolute',
    marginTop     : '0.5rem',
    marginBottom  : '0.5rem',
    color         : 'green'
  },
  sellOrder : {
    position      : 'absolute',
    marginTop     : '0.5rem',
    marginBottom  : '0.5rem',
    color         : 'red'
  }
});

const MarketWidgetElementPrice = (p: MarketWidgetElementPriceProps) => {
  const rStripRegex:  RegExp = /0+$/g,
        price:        number = (p.orderSide === "buy") ? p.marketPrices.buyPrice: p.marketPrices.sellPrice,
        classes              = useRef(ElementPriceClasses());

  const doubleClickHandler = () => {
    console.log("DOUBLE CLICK: "+p.instrumentID)
    if ( p.client && p.marketPriceWebSocket && p.marketPriceWebSocket.current && p.marketPriceWebSocket.current.readyState === WebSocket.OPEN) {
      const orderID : string = uuidv4(),
            quantity: number = Number(p.quantity);

      console.log("TRUE")
      console.log(quantity)
      if (isNaN(quantity)) {
        throw new Error("Quantity invalid: " + p.quantity);
      }

      let orderRequest: NewOrderRequest = {
        type          : "new_order",
        client_id     : p.client.id,
        order_id      : orderID,
        side          : p.orderSide,
        quantity      : quantity,
        instrument_id : p.instrumentID,
        limit         : "nan"
      };

      console.log(orderRequest)

      switch (p.orderType) {
        case "Market Order": {
          p.marketPriceWebSocket.current.send(JSON.stringify(orderRequest));
          if (p.setOrders) {
            p.setOrders(previousState => {
              let newState: { [orderID: string]: Order } = {...previousState};

              const time_str    = new Date().toISOString().replace("T"," ").replace("Z","");
              newState[orderID] = {
                instrument_id     : p.instrumentID,
                order_id          : orderID,
                initial_quantity  : quantity,
                side              : p.orderSide,
                limit             : "nan",
                filled_quantity   : 0,
                filled_price      : "nan",
                status            : "sent",
                initiation_time   : time_str,
                update_time       : time_str,
                expiration_time   : "2262-04-11 23:47:16.854775807"
              }
              return newState;
            });
          }
          break;
        }
        case "Limit Order": {
          console.log("Price:"+price)
          console.log(orderRequest)
          if ( price && isFinite(price) ) {
            orderRequest["limit"] = new Decimal(price).toFixed();
            p.marketPriceWebSocket.current.send(JSON.stringify(orderRequest));
            if (p.setOrders) {
              p.setOrders(previousState => {
                let newState: { [orderID: string]: Order } = {...previousState};

                const time_str    = new Date().toISOString().replace("T"," ").replace("Z","");
                newState[orderID] = {
                  instrument_id     : p.instrumentID,
                  order_id          : orderID,
                  initial_quantity  : quantity,
                  side              : p.orderSide,
                  limit             : price.toString(),
                  filled_quantity   : 0,
                  filled_price      : "nan",
                  status            : "sent",
                  initiation_time   : time_str,
                  update_time       : time_str,
                  expiration_time   : "2262-04-11 23:47:16.854775807"
                };
                return newState;
              });
            }
          }
          break;
        }
        default: {
          throw new Error("Unknown order type: " + p.orderType);
        }
      }
    }
  }
  
  return (
    <div className={classes.current.root}>
      <p className={(p.orderSide === "buy") ? classes.current.buyOrder : classes.current.sellOrder}>{p.orderSide}</p>
      <svg version="1.1" x="0" y="0" width="100%" height="100%" viewBox="0 0 65 40" onDoubleClick={doubleClickHandler}>
        <text x="50%" y="60%" dominantBaseline="middle" textAnchor="middle">{isFinite(price) ? price.toFixed(7).replace(rStripRegex, "") : "-"}</text>
      </svg>
    </div>
  )
}

interface StyledOrderSubmitButtonProps {
  side      : OrderSide
  onClick?  : (e: MouseEvent<HTMLButtonElement>) => void
}

const StyledOrderSubmitButton = (p: StyledOrderSubmitButtonProps) => {
  return (
    <Button 
      variant="contained"
      style={{backgroundColor: p.side === "buy" ? "green" : "#DB0000", color: "white"}}
      onClick={p.onClick}
    >Order {p.side === "buy" ? "Buy" : "Sell"}</Button>
  )
}

export interface MarketWidgetProps {
  baseAssetTicker             : string
  quantityTicker              : string
  client?                     : Client
  instrumentID                : number
  instrumentTicker            : string
  quoteCurrencyTicker         : string
  marketPriceWebSocket        : MutableRefObject<WebSocket | null>
  onCloseCallback?            : (event: any) => void
  orderBook?                  : OrderBookSnapshotResponse
  setRequestIDtoInstrumentID  : Dispatch<SetStateAction<{ [requestID: string]: number }>>
  setOrders?                  : Dispatch<SetStateAction<{ [orderID: string]: Order }>>
}

export const MarketWidget = React.memo((p: MarketWidgetProps) => {
  // Constants
  const rStripRegex:                RegExp  = /\.?0+$/g,
        EXPANDABLE_TRADING_WIDGETS: boolean = process.env.REACT_APP_EXPANDABLE_TRADING_WIDGETS === undefined || process.env.REACT_APP_EXPANDABLE_TRADING_WIDGETS.toLowerCase() === "true",
        SELECTABLE_ORDER_TYPE:      boolean = process.env.REACT_APP_SELECTABLE_ORDER_TYPE === undefined || process.env.REACT_APP_SELECTABLE_ORDER_TYPE.toLowerCase() === "true";

  // State
  const [ marketPrices, setMarketPrices ]                     = useState<MarketPrices>({
                                                                  lastUpdated: null,
                                                                  buyPrice: 0,
                                                                  sellPrice: 0,
                                                                  spread: 0
                                                                }),
        [ marketPricesExt1, setMarketPricesExt1 ]             = useState<MarketPrices>({
                                                                  lastUpdated: null,
                                                                  buyPrice: 0,
                                                                  sellPrice: 0,
                                                                  spread: 0
                                                                }),
        [ marketPricesExt2, setMarketPricesExt2 ]             = useState<MarketPrices>({
                                                                  lastUpdated: null,
                                                                  buyPrice: 0,
                                                                  sellPrice: 0,
                                                                  spread: 0
                                                                }),
        [ marketPricesExt3, setMarketPricesExt3 ]             = useState<MarketPrices>({
                                                                  lastUpdated: null,
                                                                  buyPrice: 0,
                                                                  sellPrice: 0,
                                                                  spread: 0
                                                                }),
        [ marketPricesExt4, setMarketPricesExt4 ]             = useState<MarketPrices>({
                                                                  lastUpdated: null,
                                                                  buyPrice: 0,
                                                                  sellPrice: 0,
                                                                  spread: 0
                                                                }),
        [ orderType, setOrderType ]                           = useState<OrderType>(SELECTABLE_ORDER_TYPE ? "Limit Order" : "Market Order"),
        [ limitPrice, setLimitPrice ]                         = useState<string>("0"),
        [ expanded, setExpanded ]                             = useState<boolean>(false),
        [ orderRequestParameters, setOrderRequestParameters ] = useState<OrderRequestParameters>({
                                                                  quantity: "1",
                                                                  quantityExt1: "2",
                                                                  quantityExt2: "3",
                                                                  quantityExt3: "4",
                                                                  quantityExt4: "5"
                                                                }),
        [ orderRequestSent, setOrderRequestSent ]             = useBoolean(false);

  function LimitPriceAlert() {
    alert("You must specify a limit price");
  }

  // Handlers
  const handleMarketWidgetToggle  = () => {
    setExpanded(!expanded);
  };

  const handleOrderTypeChange     = (e: ChangeEvent<{}>, value: any) => {
    switch (value) {
      case "Limit Order":
      case "Market Order": {
        setOrderType(value);
        break;
      }
      default: {
        console.log("Unknown order type selected");
        break;
      }
    }
  }

  const handleOrderQuantity       = (e: ChangeEvent<HTMLInputElement>) => {
    const numericRegex: RegExp = /^[0-9]*\.?[0-9]*$/;
    if (!(e.target.value.match(numericRegex))) {console.log("Value did not match regex: " + e.target.value); return};

    const newQuantity: number = +(e.target.value);

    if (newQuantity < 0) return;

    setOrderRequestParameters({
      quantity      : e.target.value,
      quantityExt1  : (newQuantity * 2).toFixed(10).replace(rStripRegex, ""),
      quantityExt2  : (newQuantity * 3).toFixed(10).replace(rStripRegex, ""),
      quantityExt3  : (newQuantity * 4).toFixed(10).replace(rStripRegex, ""),
      quantityExt4  : (newQuantity * 5).toFixed(10).replace(rStripRegex, ""),
    });
  }

  const handleLimitPrice          = (e: ChangeEvent<HTMLInputElement>) => {
    const numericRegex: RegExp = /^[0-9]*\.?[0-9]*$/;
    if (!(e.target.value.match(numericRegex))) return;

    try {
      new Decimal(e.target.value);
      setLimitPrice(e.target.value);
    } catch (err) {
      setLimitPrice("nan");
    }
  }

  const handlePlaceLimitOrder     = (side: OrderSide) => {

    if (p.client && p.marketPriceWebSocket && p.marketPriceWebSocket.current  && p.marketPriceWebSocket.current.readyState === WebSocket.OPEN) {
      const orderID:  string = uuidv4(),
            quantity: number = Number(orderRequestParameters.quantity);

      if (isNaN(quantity)) {
        throw new Error("Quantity invalid: " + orderRequestParameters.quantity);
      }

      if (orderType === "Limit Order" && limitPrice === "0"){
        LimitPriceAlert()
      } else {
        let orderRequest: NewOrderRequest = {
          type          : "new_order",
          client_id     : p.client.id,
          order_id      : orderID,
          side          : side,
          quantity      : quantity,
          instrument_id : p.instrumentID,
          limit         : (limitPrice) ? new Decimal(limitPrice).toFixed() : "nan"
        };

        p.marketPriceWebSocket.current.send(JSON.stringify(orderRequest));
        if (p.setOrders) {
          p.setOrders(previousState => {
            let newState: { [orderID: string]: Order } = {...previousState};

            const time_str    = new Date().toISOString().replace("T"," ").replace("Z","");
            newState[orderID] = {
              instrument_id     : p.instrumentID,
              order_id          : orderID,
              initial_quantity  : quantity,
              side              : side,
              limit             : orderRequest["limit"],
              filled_quantity   : 0,
              filled_price      : "nan",
              status            : "sent",
              initiation_time   : time_str,
              update_time       : time_str,
              expiration_time   : "2262-04-11 23:47:16.854775807"
            };
            return newState;
          });
        }
      }
    }
  }

  // Enable price streaming
  useEffect( () => {
    if (!orderRequestSent && p.instrumentID && p.client && p.marketPriceWebSocket.current && p.marketPriceWebSocket.current.readyState === WebSocket.OPEN) {
      const wsRequestUUID: string = uuidv4(),
        initMessage: OrderBookRequest = {
          type          : "request_book",
          client_id     : p.client.id,
          instrument_id : p.instrumentID,
          request_id    : wsRequestUUID,
        };
      p.setRequestIDtoInstrumentID(prevRequestIDtoInstrumentID => {
        return { ...prevRequestIDtoInstrumentID, [wsRequestUUID]: p.instrumentID};
      });
      p.marketPriceWebSocket.current.send(JSON.stringify(initMessage));

      setOrderRequestSent();
    }
  }, [orderRequestSent, p, setOrderRequestSent]);

  // Update prices
  useEffect( () => {
    let buyPrice:       number = Infinity,
        sellPrice:      number = 0,
        buyPriceExt1:   number = Infinity,
        sellPriceExt1:  number = 0,
        buyPriceExt2:   number = Infinity,
        sellPriceExt2:  number = 0,
        buyPriceExt3:   number = Infinity,
        sellPriceExt3:  number = 0,
        buyPriceExt4:   number = Infinity,
        sellPriceExt4:  number = 0;

    if ( p.orderBook && Array.isArray(p.orderBook.bids) ) {
      let candidateBidForQuantity:      OrderBookEntry | undefined,
          candidateBidForQuantityExt1:  OrderBookEntry | undefined,
          candidateBidForQuantityExt2:  OrderBookEntry | undefined,
          candidateBidForQuantityExt3:  OrderBookEntry | undefined,
          candidateBidForQuantityExt4:  OrderBookEntry | undefined;

      // Find next-highest quantity among bids for price quote
      p.orderBook.bids.forEach(( bid: OrderBookEntry ) => {
        if(!expanded && (candidateBidForQuantity !== undefined)) {
          return
        }

        if ( (bid.price !== null) && (bid.price !== undefined) ) {
          if (!isNaN(Number(orderRequestParameters.quantity)) && (Number(orderRequestParameters.quantity) <= bid.quantity) && (candidateBidForQuantity === undefined || bid.quantity < candidateBidForQuantity.quantity)) {
            candidateBidForQuantity = bid
            if(!expanded) {
              return
            }
          }
          if (!isNaN(Number(orderRequestParameters.quantityExt1)) && (Number(orderRequestParameters.quantityExt1) <= bid.quantity) && (candidateBidForQuantityExt1 === undefined || bid.quantity < candidateBidForQuantityExt1.quantity)) {
            candidateBidForQuantityExt1 = bid
          }
          if (!isNaN(Number(orderRequestParameters.quantityExt2)) && (Number(orderRequestParameters.quantityExt2) <= bid.quantity) && (candidateBidForQuantityExt2 === undefined || bid.quantity < candidateBidForQuantityExt2.quantity)) {
            candidateBidForQuantityExt2 = bid
          }
          if (!isNaN(Number(orderRequestParameters.quantityExt3)) && (Number(orderRequestParameters.quantityExt3) <= bid.quantity) && (candidateBidForQuantityExt3 === undefined || bid.quantity < candidateBidForQuantityExt3.quantity)) {
            candidateBidForQuantityExt3 = bid
          }
          if (!isNaN(Number(orderRequestParameters.quantityExt4)) && (Number(orderRequestParameters.quantityExt4) <= bid.quantity) && (candidateBidForQuantityExt4 === undefined || bid.quantity < candidateBidForQuantityExt4.quantity)) {
            candidateBidForQuantityExt4 = bid
          }
        }
      });

      if (candidateBidForQuantity !== undefined) {
        sellPrice = candidateBidForQuantity.price;
      }
      if (candidateBidForQuantityExt1 !== undefined) {
        sellPriceExt1 = candidateBidForQuantityExt1.price;
      }
      if (candidateBidForQuantityExt2 !== undefined) {
        sellPriceExt2 = candidateBidForQuantityExt2.price;
      }
      if (candidateBidForQuantityExt3 !== undefined) {
        sellPriceExt3 = candidateBidForQuantityExt3.price;
      }
      if (candidateBidForQuantityExt4 !== undefined) {
        sellPriceExt4 = candidateBidForQuantityExt4.price;
      }
    }
  
    if ( p.orderBook && Array.isArray(p.orderBook.asks) ) {
      let candidateAskForQuantity:      OrderBookEntry | undefined,
          candidateAskForQuantityExt1:  OrderBookEntry | undefined,
          candidateAskForQuantityExt2:  OrderBookEntry | undefined,
          candidateAskForQuantityExt3:  OrderBookEntry | undefined,
          candidateAskForQuantityExt4:  OrderBookEntry | undefined;
      
      // Find next-highest quantity among asks for price quote
      p.orderBook.asks.forEach(( ask: OrderBookEntry ) => {
        if(!expanded && (candidateAskForQuantity !== undefined)) {
          return
        }

        if ( (ask.price !== null) && (ask.price !== undefined) ) {
          if (!isNaN(Number(orderRequestParameters.quantity)) && (Number(orderRequestParameters.quantity) <= ask.quantity) && (candidateAskForQuantity === undefined || ask.quantity < candidateAskForQuantity.quantity)) {
            candidateAskForQuantity = ask
            if(!expanded) {
              return
            }
          }
          if (!isNaN(Number(orderRequestParameters.quantityExt1)) && (Number(orderRequestParameters.quantityExt1) <= ask.quantity) && (candidateAskForQuantityExt1 === undefined || ask.quantity < candidateAskForQuantityExt1.quantity)) {
            candidateAskForQuantityExt1 = ask
          }
          if (!isNaN(Number(orderRequestParameters.quantityExt2)) && (Number(orderRequestParameters.quantityExt2) <= ask.quantity) && (candidateAskForQuantityExt2 === undefined || ask.quantity < candidateAskForQuantityExt2.quantity)) {
            candidateAskForQuantityExt2 = ask
          }
          if (!isNaN(Number(orderRequestParameters.quantityExt3)) && (Number(orderRequestParameters.quantityExt3) <= ask.quantity) && (candidateAskForQuantityExt3 === undefined || ask.quantity < candidateAskForQuantityExt3.quantity)) {
            candidateAskForQuantityExt3 = ask
          }
          if (!isNaN(Number(orderRequestParameters.quantityExt4)) && (Number(orderRequestParameters.quantityExt4) <= ask.quantity) && (candidateAskForQuantityExt4 === undefined || ask.quantity < candidateAskForQuantityExt4.quantity)) {
            candidateAskForQuantityExt4 = ask
          }
        }
      });

      if (candidateAskForQuantity !== undefined) {
        buyPrice = candidateAskForQuantity.price;
      }
      if (candidateAskForQuantityExt1 !== undefined) {
        buyPriceExt1 = candidateAskForQuantityExt1.price;
      }
      if (candidateAskForQuantityExt2 !== undefined) {
        buyPriceExt2 = candidateAskForQuantityExt2.price;
      }
      if (candidateAskForQuantityExt3 !== undefined) {
        buyPriceExt3 = candidateAskForQuantityExt3.price;
      }
      if (candidateAskForQuantityExt4 !== undefined) {
        buyPriceExt4 = candidateAskForQuantityExt4.price;
      }
    }

    setMarketPrices(previousState => {
      return ((previousState.buyPrice === buyPrice) && (previousState.sellPrice === sellPrice)) ? previousState : {
        lastUpdated : new Date(),
        buyPrice    : (!isNaN(Number(orderRequestParameters.quantity)) && orderRequestParameters.quantity) ? buyPrice : 0,
        sellPrice   : (!isNaN(Number(orderRequestParameters.quantity)) && orderRequestParameters.quantity) ? sellPrice : Infinity,
        spread      : (!isNaN(Number(orderRequestParameters.quantity)) && orderRequestParameters.quantity) ? (buyPrice - sellPrice) : Infinity
      };
    });

    if( expanded ) {
      setMarketPricesExt1(previousState => {
        return ((previousState.buyPrice === buyPriceExt1) && (previousState.sellPrice === sellPriceExt1)) ? previousState : {
          lastUpdated : new Date(),
          buyPrice    : (!isNaN(Number(orderRequestParameters.quantityExt1)) && orderRequestParameters.quantityExt1) ? buyPriceExt1 : 0,
          sellPrice   : (!isNaN(Number(orderRequestParameters.quantityExt1)) && orderRequestParameters.quantityExt1) ? sellPriceExt1 : Infinity,
          spread      : (!isNaN(Number(orderRequestParameters.quantityExt1)) && orderRequestParameters.quantityExt1) ? (buyPriceExt1 - sellPriceExt1) : Infinity
        }
      });
      setMarketPricesExt2(previousState => {
        return ((previousState.buyPrice === buyPriceExt2) && (previousState.sellPrice === sellPriceExt2)) ? previousState : {
          lastUpdated : new Date(),
          buyPrice    : (!isNaN(Number(orderRequestParameters.quantityExt2)) && orderRequestParameters.quantityExt2) ? buyPriceExt2 : 0,
          sellPrice   : (!isNaN(Number(orderRequestParameters.quantityExt2)) && orderRequestParameters.quantityExt2) ? sellPriceExt2 : Infinity,
          spread      : (!isNaN(Number(orderRequestParameters.quantityExt2)) && orderRequestParameters.quantityExt2) ? (buyPriceExt2 - sellPriceExt2) : Infinity
        }
      });
      setMarketPricesExt3(previousState => {
        return ((previousState.buyPrice === buyPriceExt3) && (previousState.sellPrice === sellPriceExt3)) ? previousState : {
          lastUpdated : new Date(),
          buyPrice    : (!isNaN(Number(orderRequestParameters.quantityExt3)) && orderRequestParameters.quantityExt3) ? buyPriceExt3 : 0,
          sellPrice   : (!isNaN(Number(orderRequestParameters.quantityExt3)) && orderRequestParameters.quantityExt3) ? sellPriceExt3 : Infinity,
          spread      : (!isNaN(Number(orderRequestParameters.quantityExt3)) && orderRequestParameters.quantityExt3) ? (buyPriceExt3 - sellPriceExt3) : Infinity
        }
      });
      setMarketPricesExt4(previousState => {
        return ((previousState.buyPrice === buyPriceExt4) && (previousState.sellPrice === sellPriceExt4)) ? previousState : {
          lastUpdated : new Date(),
          buyPrice    : (!isNaN(Number(orderRequestParameters.quantityExt4)) && orderRequestParameters.quantityExt4) ? buyPriceExt4 : 0,
          sellPrice   : (!isNaN(Number(orderRequestParameters.quantityExt4)) && orderRequestParameters.quantityExt4) ? sellPriceExt4 : Infinity,
          spread      : (!isNaN(Number(orderRequestParameters.quantityExt4)) && orderRequestParameters.quantityExt4) ? (buyPriceExt4 - sellPriceExt4) : Infinity
        }
      })
    };
  }, [orderRequestParameters, p, expanded]);

  return (
    <MarketWidgetElement>
      <MarketWidgetElementRow>
        <MarketWidgetElementInstrument>{p.instrumentTicker}</MarketWidgetElementInstrument>
        <MarketWidgetElementCloseButton id={p.instrumentTicker} onClick={(e) => {if (p.onCloseCallback) {p.onCloseCallback(e);}}}>✖</MarketWidgetElementCloseButton>{/* style={{color: isDarkMode ? "#FFF" : "#000"}}  */}
      </MarketWidgetElementRow>
      <MarketWidgetElementRow>
        <MarketWidgetElementPrice
          client={p.client}
          instrumentID={p.instrumentID}
          marketPrices={marketPrices}
          marketPriceWebSocket={p.marketPriceWebSocket}
          orderSide={"sell"}
          orderType={orderType}
          quantity={isNaN(Number(orderRequestParameters.quantity)) ? 0 : Number(orderRequestParameters.quantity)}
          setOrders={p.setOrders}/>
        <MarketWidgetSpread>
          <svg version="1.1" x="0" y="0" width="100%" height="100%" viewBox="0 0 50 25">
            <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" style={{color: 'blue'}}>{isFinite(marketPrices.spread) ? marketPrices.spread.toFixed(6).replace(rStripRegex, ''): "-"}</text>
          </svg>
        </MarketWidgetSpread>
        <MarketWidgetElementPrice
          client={p.client}
          instrumentID={p.instrumentID}
          marketPrices={marketPrices}
          marketPriceWebSocket={p.marketPriceWebSocket}
          orderSide={"buy"}
          orderType={orderType}
          quantity={isNaN(Number(orderRequestParameters.quantity)) ? 0 : Number(orderRequestParameters.quantity)}
          setOrders={p.setOrders}/>
      </MarketWidgetElementRow>
      <MarketWidgetElementLastUpdated>{marketPrices.lastUpdated?.toLocaleTimeString()}</MarketWidgetElementLastUpdated>
      {
        SELECTABLE_ORDER_TYPE &&
        <Tabs indicatorColor="primary" textColor="primary" value={orderType} onChange={handleOrderTypeChange}>
          <Tab label="Market" value="Market Order"/>
          <Tab label="Limit" value="Limit Order"/>
        </Tabs>
      }
      <MarketWidgetOrderQuantity
        onChangeHandler={handleOrderQuantity}
        instrumentTicker={p.instrumentTicker}
        quantityTicker={p.quantityTicker}
      />
      {
        SELECTABLE_ORDER_TYPE &&
        <Collapse in={orderType === "Limit Order"}>
          <>
            <MarketWidgetLimitPrice
              value={limitPrice}
              baseAssetTicker={p.baseAssetTicker}
              quoteCurrencyTicker={p.quoteCurrencyTicker}
              instrumentTicker={p.instrumentTicker}
              onChangeHandler={handleLimitPrice}
            />
            <MarketWidgetElementRowSeparated>
              <StyledOrderSubmitButton side="sell" onClick={() => {handlePlaceLimitOrder("sell")}}/>
              <StyledOrderSubmitButton side="buy" onClick={() => {handlePlaceLimitOrder("buy")}}/>
            </MarketWidgetElementRowSeparated>
          </>
        </Collapse>}
      {EXPANDABLE_TRADING_WIDGETS && <MarketWidgetElementRowSeparated>
        <ToggleButton size="small" aria-label="toggle market" value={false} onChange={handleMarketWidgetToggle}><MenuIcon /></ToggleButton>
      </MarketWidgetElementRowSeparated>}
      <Collapse in={expanded}>
        <MarketWidgetExtendedFields
          client={p.client}
          instrumentID={p.instrumentID}
          instrumentTicker={p.instrumentTicker}
          marketPricesExt1={marketPricesExt1}
          marketPricesExt2={marketPricesExt2}
          marketPricesExt3={marketPricesExt3}
          marketPricesExt4={marketPricesExt4}
          orderType={orderType}
          orderRequestParameters={orderRequestParameters}
          orderRequestParameterChangeHandler={setOrderRequestParameters}
          marketPriceWebSocket={p.marketPriceWebSocket}
          setOrders={p.setOrders}
        />
      </Collapse>
    </MarketWidgetElement>
  )
}, (prevProps: MarketWidgetProps, newProps: MarketWidgetProps) => {
  if( prevProps.orderBook === newProps.orderBook ) {
    return true;
  } else {
    return false;
  }
})

interface marketWidgetExtendedFieldsProps {
  client?                             : Client
  instrumentID                        : number
  instrumentTicker                    : string
  marketPricesExt1                    : MarketPrices
  marketPricesExt2                    : MarketPrices
  marketPricesExt3                    : MarketPrices
  marketPricesExt4                    : MarketPrices
  orderType                           : OrderType
  orderRequestParameters              : OrderRequestParameters
  orderRequestParameterChangeHandler  : Dispatch<SetStateAction<OrderRequestParameters>>
  marketPriceWebSocket                : MutableRefObject<WebSocket | null>
  setOrders?                          : Dispatch<SetStateAction<{ [orderID: string]: Order }>>
}

const ExtendedFieldsStyles = makeStyles({
                                          orderQuantity: {
                                            width                     : '38%',
                                            '& .MuiOutlinedInput-root': {
                                              'font-size': 'small'
                                            },
                                            marginTop                 : 'auto',
                                            marginBottom              : 'auto',
                                            marginRight               : '2%',
                                            backgroundColor           : 'white'
                                          }
                                        });

const MarketWidgetExtendedFields = (p: marketWidgetExtendedFieldsProps) =>  {
  const rStripRegex: RegExp     = /\.?0+$/g,
        classes                 = useRef(ExtendedFieldsStyles()),
        sellPriceExt1:  string  = isFinite(p.marketPricesExt1.sellPrice) ? p.marketPricesExt1.sellPrice.toFixed(6).replace(rStripRegex, "") : "-",
        buyPriceExt1:   string  = isFinite(p.marketPricesExt1.buyPrice) ? p.marketPricesExt1.buyPrice.toFixed(6).replace(rStripRegex, "") : "-",
        sellPriceExt2:  string  = isFinite(p.marketPricesExt1.sellPrice) ? p.marketPricesExt2.sellPrice.toFixed(6).replace(rStripRegex, "") : "-",
        buyPriceExt2:   string  = isFinite(p.marketPricesExt1.buyPrice) ? p.marketPricesExt2.buyPrice.toFixed(6).replace(rStripRegex, "") : "-",
        sellPriceExt3:  string  = isFinite(p.marketPricesExt1.sellPrice) ? p.marketPricesExt3.sellPrice.toFixed(6).replace(rStripRegex, "") : "-",
        buyPriceExt3:   string  = isFinite(p.marketPricesExt1.buyPrice) ? p.marketPricesExt3.buyPrice.toFixed(6).replace(rStripRegex, "") : "-",
        sellPriceExt4:  string  = isFinite(p.marketPricesExt1.sellPrice) ? p.marketPricesExt4.sellPrice.toFixed(6).replace(rStripRegex, "") : "-",
        buyPriceExt4:   string  = isFinite(p.marketPricesExt1.buyPrice) ? p.marketPricesExt4.buyPrice.toFixed(6).replace(rStripRegex, "") : "-";

  // Handlers
  interface doubleClickHandlerParams {
    client?               : Client
    instrumentID          : number
    limit                 : string
    marketPriceWebSocket  : MutableRefObject<WebSocket | null>
    orderType             : OrderType
    side                  : OrderSide
    quantity              : string
    setOrders?            : Dispatch<SetStateAction<{ [orderID: string]: Order }>>
  }

  function LimitPriceAlert() {
    alert("You must specify a limit price");
  }

  const doubleClickHandler = (p: doubleClickHandlerParams) => {
    if (p.client && p.marketPriceWebSocket && p.marketPriceWebSocket.current  && p.marketPriceWebSocket.current.readyState === WebSocket.OPEN) {
      const orderID:  string = uuidv4(),
            quantity: number = Number(p.quantity);

      if (isNaN(quantity)) {
        throw new Error("Quantity invalid: " + p.quantity);
      }

      if (p.orderType=== "Limit Order" && p.limit === "0"){
        LimitPriceAlert()
      } else {
        let orderRequest: NewOrderRequest = {
          type          : "new_order",
          client_id     : p.client.id,
          order_id      : orderID,
          side          : p.side,
          quantity      : quantity,
          instrument_id : p.instrumentID,
          limit         : "nan"
        };

        switch (p.orderType) {
          case "Market Order": {
            p.marketPriceWebSocket.current.send(JSON.stringify(orderRequest));
            if (p.setOrders) {
              p.setOrders(previousState => {
                let newState: { [orderID: string]: Order } = {...previousState};

                const time_str    = new Date().toISOString().replace("T"," ").replace("Z","");
                newState[orderID] = {
                  instrument_id     : p.instrumentID,
                  order_id          : orderID,
                  initial_quantity  : quantity,
                  side              : p.side,
                  limit             : "nan",
                  filled_quantity   : 0,
                  filled_price      : "nan",
                  status            : "sent",
                  initiation_time   : time_str,
                  update_time       : time_str,
                  expiration_time   : time_str
                };
                return newState;
              });
            }
            break;
          }
          case "Limit Order": {
            orderRequest["limit"] = new Decimal(p.limit).toFixed();
            p.marketPriceWebSocket.current.send(JSON.stringify(orderRequest));
            if (p.setOrders) {
              p.setOrders(previousState => {
                let newState: { [orderID: string]: Order } = {...previousState};
                const time_str    = new Date().toISOString().replace("T"," ").replace("Z","");
                newState[orderID] = {
                  instrument_id     : p.instrumentID,
                  order_id          : orderID,
                  initial_quantity  : quantity,
                  side              : p.side,
                  limit             : (p.limit) ? p.limit : "nan",
                  filled_quantity   : 0,
                  filled_price      : "nan",
                  status            : "sent",
                  initiation_time   : time_str,
                  update_time       : time_str,
                  expiration_time   : "2262-04-11 23:47:16.854775807"
                };
                return newState;
              });
            }
            break;
          }
          default: {
            throw new Error("Unknown order type: " + p.orderType);
          }
        }
      }
    }
  }

  return (
    <>
      <MarketWidgetElementRowSeparated>
        <TextField
          id={"MarketWidgetOrderQuantityExt1_" + p.instrumentTicker}
          value={p.orderRequestParameters.quantityExt1}
          size="small"
          variant="outlined"
          className={classes.current.orderQuantity}
          onChange={(e) => {
            p.orderRequestParameterChangeHandler({...p.orderRequestParameters, quantityExt1: e.target.value})
          }}
          InputProps={{ inputProps: { min: 0, pattern: "^[0-9]*.?[0-9]*$" } }}
        />
        <MarketWidgetOrderQuantityExtSellPrice onDoubleClick={() => {
          doubleClickHandler({
            client                : p.client,
            instrumentID          : p.instrumentID,
            limit                 : sellPriceExt1,
            marketPriceWebSocket  : p.marketPriceWebSocket,
            orderType             : p.orderType,
            side                  : "sell",
            quantity              : p.orderRequestParameters.quantityExt1,
            setOrders             : p.setOrders
          })
        }}>
        {sellPriceExt1}
        </MarketWidgetOrderQuantityExtSellPrice>
        <MarketWidgetOrderQuantityExtSpread>{(isFinite(p.marketPricesExt1.spread)) ? p.marketPricesExt1.spread.toFixed(4).replace(rStripRegex, "") : "-"}</MarketWidgetOrderQuantityExtSpread>
        <MarketWidgetOrderQuantityExtBuyPrice onDoubleClick={() => {
          doubleClickHandler({
            client                : p.client,
            instrumentID          : p.instrumentID,
            limit                 : buyPriceExt1,
            marketPriceWebSocket  : p.marketPriceWebSocket,
            orderType             : p.orderType,
            side                  : "buy",
            quantity              : p.orderRequestParameters.quantityExt1,
            setOrders             : p.setOrders
          })
        }}>
          {buyPriceExt1}
        </MarketWidgetOrderQuantityExtBuyPrice>
      </MarketWidgetElementRowSeparated>
      <MarketWidgetElementRowSeparated>
        <TextField
          id={"MarketWidgetOrderQuantityExt2_" + p.instrumentTicker}
          value={p.orderRequestParameters.quantityExt2}
          size="small"
          variant="outlined"
          className={classes.current.orderQuantity}
          onChange={(e) => {
            p.orderRequestParameterChangeHandler({...p.orderRequestParameters, quantityExt2: e.target.value})
          }}
          InputProps={{ inputProps: { min: 0, pattern: "^[0-9]*.?[0-9]*$" } }}
        />
        <MarketWidgetOrderQuantityExtSellPrice onDoubleClick={() => {
          doubleClickHandler({
            client                : p.client,
            instrumentID          : p.instrumentID,
            limit                 : sellPriceExt2,
            marketPriceWebSocket  : p.marketPriceWebSocket,
            orderType             : p.orderType,
            side                  : "sell",
            quantity              : p.orderRequestParameters.quantityExt2,
            setOrders             : p.setOrders
          })
        }}>
          {sellPriceExt2}
        </MarketWidgetOrderQuantityExtSellPrice>
        <MarketWidgetOrderQuantityExtSpread>{(isFinite(p.marketPricesExt2.spread)) ? p.marketPricesExt2.spread.toFixed(4).replace(rStripRegex, "") : "-"}</MarketWidgetOrderQuantityExtSpread>
        <MarketWidgetOrderQuantityExtBuyPrice onDoubleClick={() => {
          doubleClickHandler({
            client                : p.client,
            instrumentID          : p.instrumentID,
            limit                 : buyPriceExt1,
            marketPriceWebSocket  : p.marketPriceWebSocket,
            orderType             : p.orderType,
            side                  : "buy",
            quantity              : p.orderRequestParameters.quantityExt2,
            setOrders             : p.setOrders
          })
        }}>
          {buyPriceExt2}
        </MarketWidgetOrderQuantityExtBuyPrice>
      </MarketWidgetElementRowSeparated>
      <MarketWidgetElementRowSeparated>
        <TextField
          id={"MarketWidgetOrderQuantityExt3_" + p.instrumentTicker}
          value={p.orderRequestParameters.quantityExt3}
          size="small"
          variant="outlined"
          className={classes.current.orderQuantity}
          onChange={(e) => {
            p.orderRequestParameterChangeHandler({...p.orderRequestParameters, quantityExt3: e.target.value})
          }}
          InputProps={{ inputProps: { min: 0, pattern: "^[0-9]*.?[0-9]*$" } }}
        />
        <MarketWidgetOrderQuantityExtSellPrice onDoubleClick={() => {
          doubleClickHandler({
            client                : p.client,
            instrumentID          : p.instrumentID,
            limit                 : sellPriceExt3,
            marketPriceWebSocket  : p.marketPriceWebSocket,
            orderType             : p.orderType,
            side                  : "sell",
            quantity              : p.orderRequestParameters.quantityExt3,
            setOrders             : p.setOrders
          })
        }}>
          {sellPriceExt3}
        </MarketWidgetOrderQuantityExtSellPrice>
        <MarketWidgetOrderQuantityExtSpread>{(isFinite(p.marketPricesExt3.spread)) ? p.marketPricesExt3.spread.toFixed(4).replace(rStripRegex, "") : "-"}</MarketWidgetOrderQuantityExtSpread>
        <MarketWidgetOrderQuantityExtBuyPrice onDoubleClick={() => {
          doubleClickHandler({
            client                : p.client,
            instrumentID          : p.instrumentID,
            limit                 : buyPriceExt3,
            marketPriceWebSocket  : p.marketPriceWebSocket,
            orderType             : p.orderType,
            side                  : "buy",
            quantity              : p.orderRequestParameters.quantityExt3,
            setOrders             : p.setOrders
          })
        }}>
          {buyPriceExt3}
        </MarketWidgetOrderQuantityExtBuyPrice>
      </MarketWidgetElementRowSeparated>
      <MarketWidgetElementRowSeparated>
        <TextField
          id={"MarketWidgetOrderQuantityExt4_" + p.instrumentTicker}
          value={p.orderRequestParameters.quantityExt4}
          size="small"
          variant="outlined"
          className={classes.current.orderQuantity}
          onChange={(e) => {
            p.orderRequestParameterChangeHandler({...p.orderRequestParameters, quantityExt4: e.target.value})
          }}
          InputProps={{ inputProps: { min: 0, pattern: "^[0-9]*.?[0-9]*$" } }}
        />
        <MarketWidgetOrderQuantityExtSellPrice onDoubleClick={() => {
          doubleClickHandler({
            client                : p.client,
            instrumentID          : p.instrumentID,
            limit                 : sellPriceExt4,
            marketPriceWebSocket  : p.marketPriceWebSocket,
            orderType             : p.orderType,
            side                  : "sell",
            quantity              : p.orderRequestParameters.quantityExt4,
            setOrders             : p.setOrders
          })
        }}>
          {sellPriceExt4}
        </MarketWidgetOrderQuantityExtSellPrice>
        <MarketWidgetOrderQuantityExtSpread>{(isFinite(p.marketPricesExt4.spread)) ? p.marketPricesExt4.spread.toFixed(4).replace(rStripRegex, "") : "-"}</MarketWidgetOrderQuantityExtSpread>
        <MarketWidgetOrderQuantityExtBuyPrice onDoubleClick={() => {
          doubleClickHandler({
            client                : p.client,
            instrumentID          : p.instrumentID,
            limit                 : buyPriceExt4,
            marketPriceWebSocket  : p.marketPriceWebSocket,
            orderType             : p.orderType,
            side                  : "buy",
            quantity              : p.orderRequestParameters.quantityExt4,
            setOrders             : p.setOrders
          })
        }}>
          {buyPriceExt4}
        </MarketWidgetOrderQuantityExtBuyPrice>
      </MarketWidgetElementRowSeparated>
    </>
  )
}
